import React from 'react';
import { Theme } from '@mui/material';
import Pill from 'components/Pill';
import { capitalize, formatSnakeCaseToSentenceCase } from 'services/string';
import { SHIPPING_BLANK_TABLE_CELL_VALUE } from './constants';

// ====== Shipment Status Utilities ======

// Define the shipment status color mapping
export const getShipmentStatusColor = (
  status: string,
  theme: Theme
): string => {
  const SHIPMENT_STATUS_COLORS: { [key: string]: string } = {
    EXCEPTION: theme.palette.colors.chiGong,
    IN_TRANSIT: theme.palette.colors.chiGong,
    DELIVERED: theme.palette.colors.lushParadise,
    // Default color for other statuses
    DEFAULT: theme.palette.colors.pelorous,
  };

  return SHIPMENT_STATUS_COLORS[status] || SHIPMENT_STATUS_COLORS.DEFAULT;
};

// Extract shipment information from orderItem
export const getShipmentInfo = (
  orderItem: any
): {
  shipmentId: string | null;
  trackingNumber: string | null;
  trackingLink: string | null;
  expectedDeliveryDate: string | null;
  status: string | null;
  substatus: string | null;
  substatusDescription: string | null;
  shipmentCarrierId: string | null;
} => {
  // Default return object with null values
  const defaultInfo = {
    shipmentId: null,
    trackingNumber: null,
    trackingLink: null,
    expectedDeliveryDate: null,
    status: null,
    substatus: null,
    substatusDescription: null,
    shipmentCarrierId: null,
  };

  // Check if shipmentItems array exists and has items
  if (orderItem.shipmentItems && orderItem.shipmentItems.length > 0) {
    const sortedShipmentItems = [...orderItem.shipmentItems].sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return dateB - dateA; // Sort in descending order (newest first)
    });
    const shipmentItem = sortedShipmentItems[0];
    const { shipment } = shipmentItem;

    return {
      shipmentId: shipmentItem.shipmentId,
      trackingNumber:
        shipment.trackings && shipment.trackings.length > 0
          ? shipment.trackings[0].trackingNumber
          : null,
      trackingLink:
        shipment.trackings && shipment.trackings.length > 0
          ? shipment.trackings[0].trackingLink
          : null,
      expectedDeliveryDate:
        shipment.trackings && shipment.trackings.length > 0
          ? shipment.trackings[0].expectedDeliveryDate
          : null,
      status: shipment.status || null,
      substatus: shipment.substatus || null,
      substatusDescription: shipment.substatusDescription || null,
      shipmentCarrierId: shipment.shipmentCarrierId || null,
    };
  }

  // Fall back to shipment object if it exists
  if (orderItem.shipment) {
    const { shipment } = orderItem;

    return {
      shipmentId: shipment.id || null,
      trackingNumber:
        shipment.trackings && shipment.trackings.length > 0
          ? shipment.trackings[0].trackingNumber
          : null,
      trackingLink:
        shipment.trackings && shipment.trackings.length > 0
          ? shipment.trackings[0].trackingLink
          : null,
      expectedDeliveryDate:
        shipment.trackings && shipment.trackings.length > 0
          ? shipment.trackings[0].expectedDeliveryDate
          : null,
      status: shipment.status || null,
      substatus: shipment.substatus || null,
      substatusDescription: shipment.substatusDescription || null,
      shipmentCarrierId: shipment.shipmentCarrierId || null,
    };
  }

  // Return default object with null values if no shipment info found
  return defaultInfo;
};

// Format the shipment status text
export const formatShipmentStatusText = (
  shipmentStatus?: string,
  shipmentSubStatus?: string
): string => {
  const shipmentStatusText = (shipmentSubStatus || shipmentStatus) ?? 'PENDING';
  return capitalize(formatSnakeCaseToSentenceCase(shipmentStatusText));
};

// Render the shipment status pill component
export const renderShipmentStatusPill = (
  shipmentStatus?: string,
  shipmentSubStatus?: string,
  shipmentStatusDescription?: string,
  theme?: Theme,
  additionalProps?: Record<string, any>
): React.ReactElement => {
  if (!shipmentStatus && !shipmentSubStatus) {
    return React.createElement('div', {}, SHIPPING_BLANK_TABLE_CELL_VALUE);
  }

  const pillText = formatShipmentStatusText(shipmentStatus, shipmentSubStatus);
  const statusForColor = shipmentStatus || 'DEFAULT';

  return React.createElement(Pill, {
    text: pillText,
    toolTipHoverText: pillText,
    toolTipArrow: true,
    toolTipPlacement: 'right',
    color: theme ? getShipmentStatusColor(statusForColor, theme) : '',
    textColor: theme?.palette.colors.white || '',
    ...additionalProps,
  });
};
